import { StateMachine } from "@/core/arch/state_machine";
import type { MoneyRepository } from "./money_repository";
import type { Invoice, Transaction, InvoiceFilter } from "./types";
import { defaultInvoiceFilter } from "./types";

const PAGE_SIZE = 50;
const DRAIN_LIMIT = 20;

export interface InvoicesListState {
  items: Invoice[];
  /** Map of invoiceId → Transaction for all fetched invoices (the "Paid via" join). */
  paidViaMap: Map<string, Transaction>;
  /** True if the "Paid via" batch lookup failed. */
  paidViaError: boolean;
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
  filters: InvoiceFilter;
  hasNext: boolean;
  hasPrev: boolean;
}

export class InvoicesListBloc extends StateMachine<InvoicesListState> {
  private _loadToken = 0;
  private _pageTokens: (string | undefined)[] = [undefined];
  private _nextToken: string | null = null;

  constructor(
    private repo: MoneyRepository,
    private businessId: string,
  ) {
    super({
      items: [],
      paidViaMap: new Map(),
      paidViaError: false,
      status: "idle",
      filters: defaultInvoiceFilter(),
      hasNext: false,
      hasPrev: false,
    });
  }

  async load(): Promise<void> {
    this._pageTokens = [undefined];
    this._nextToken = null;
    await this._fetchCurrentPage();
  }

  async nextPage(): Promise<void> {
    if (this.state.status === "loading") return;
    if (!this.state.hasNext || this._nextToken == null) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.push(this._nextToken);
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async prevPage(): Promise<void> {
    if (this.state.status === "loading") return;
    if (!this.state.hasPrev) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.pop();
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async setFilter(patch: Partial<InvoiceFilter>): Promise<void> {
    this.setState({
      ...this.state,
      filters: { ...this.state.filters, ...patch },
    });
    await this.load();
  }

  private async _fetchCurrentPage(): Promise<boolean> {
    const token = ++this._loadToken;
    this.setState({
      ...this.state,
      status: "loading",
      paidViaMap: new Map(),
      paidViaError: false,
      error: undefined,
    });
    const entryToken = this._pageTokens[this._pageTokens.length - 1];
    const filter = this.state.filters;

    const fetchPage = async (
      pageToken: string | undefined,
    ): Promise<{ items: Invoice[]; nextToken: string | null }> => {
      const result = await this.repo.listInvoices({
        businessId: this.businessId,
        limit: PAGE_SIZE,
        nextToken: pageToken,
        filter,
      });
      return { items: result.items, nextToken: result.nextToken ?? null };
    };

    try {
      let page = await fetchPage(entryToken);
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;

      let drains = 0;
      while (
        page.items.length === 0 &&
        page.nextToken != null &&
        drains < DRAIN_LIMIT
      ) {
        page = await fetchPage(page.nextToken);
        // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
        if (token !== this._loadToken) return false;
        drains += 1;
      }

      this._nextToken = page.nextToken;

      const invoiceIds = page.items.map((inv) => inv.id);
      let paidViaMap = new Map<string, Transaction>();
      let paidViaError = false;

      try {
        paidViaMap =
          await this.repo.batchLookupTransactionsForInvoices(invoiceIds);
      } catch {
        // Silent degradation — show "—" in every Paid via cell; surface muted banner
        paidViaError = true;
      }

      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;

      this.setState({
        ...this.state,
        items: page.items,
        paidViaMap,
        paidViaError,
        status: "loaded",
        error: undefined,
        hasNext: this._nextToken != null,
        hasPrev: this._pageTokens.length > 1,
      });
      return true;
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;
      this._nextToken = null;
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load invoices",
        hasNext: false,
        hasPrev: false,
      });
      return false;
    }
  }
}
